// 관리자 대시보드.
// 작업계획서 5.6 통계: 일/주 완료 주문 수·매출(현장 결제 기준), 베스트 상품, 신규 주문 수.

import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import { getStats } from "@/features/members/membersApi.ts";

// 관리자 대시보드 화면 (오늘/주간 실적 통계 요약).
export default function DashboardPage() {
  const statsQuery = useQuery({
    queryKey: ["admin-stats"],
    queryFn: getStats,
    refetchInterval: 30_000,
  });
  const s = statsQuery.data;
  const hasPending = (s?.pendingOrders ?? 0) > 0;

  return (
    <main className="mx-auto flex max-w-screen-lg flex-col gap-5 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader title="대시보드" description="오늘/최근 7일 완료 실적과 처리 대기 주문을 한눈에 봅니다." />

      {/* 신규 주문 안내: 대기 건이 있으면 브랜드색으로 주의를 끈다. */}
      <Link
        to="/orders"
        className={`flex items-center justify-between rounded-lg border p-4 transition-colors duration-fast ${
          hasPending
            ? "border-brand-300 bg-brand-50 hover:bg-brand-100"
            : "border-ink-200 bg-surface hover:bg-ink-50"
        }`}
      >
        <span className="text-sm font-medium text-ink-700">처리 대기 신규 주문</span>
        <span className="flex items-center gap-1">
          <span className={`text-2xl font-bold tabular-nums ${hasPending ? "text-brand-700" : "text-ink-900"}`}>
            {statsQuery.isLoading ? "..." : `${s?.pendingOrders ?? 0}건`}
          </span>
          <ChevronRight size={20} aria-hidden="true" className="text-ink-600" />
        </span>
      </Link>

      {/* 매출 카드 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          title="오늘 (완료 기준)"
          orders={s?.today.orders}
          salesKrw={s?.today.salesKrw}
          loading={statsQuery.isLoading}
        />
        <StatCard
          title="최근 7일 (완료 기준)"
          orders={s?.week.orders}
          salesKrw={s?.week.salesKrw}
          loading={statsQuery.isLoading}
        />
      </div>

      {/* 베스트 상품 */}
      <Card as="section">
        <h2 className="mb-2 text-base font-bold text-ink-900">베스트 상품 (누적 판매량)</h2>
        {statsQuery.isLoading ? (
          <p className="text-sm text-ink-600">불러오는 중...</p>
        ) : (s?.bestProducts.length ?? 0) === 0 ? (
          <p className="text-sm text-ink-600">판매 데이터가 아직 없습니다.</p>
        ) : (
          <ol className="flex flex-col gap-1">
            {s?.bestProducts.map((p, i) => (
              <li key={p.id} className="flex items-center justify-between text-sm">
                <span className="truncate text-ink-900">
                  <span className="mr-2 font-bold text-brand-600">{i + 1}</span>
                  {p.name}
                </span>
                <span className="shrink-0 tabular-nums text-ink-600">{p.salesCount.toLocaleString()}개</span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <p className="text-xs text-ink-600">매출은 &apos;완료&apos;로 종결된 주문의 현장 결제 합계 기준입니다.</p>
    </main>
  );
}

function StatCard({
  title,
  orders,
  salesKrw,
  loading,
}: {
  title: string;
  orders?: number;
  salesKrw?: number;
  loading: boolean;
}) {
  return (
    <Card>
      <p className="text-sm text-ink-600">{title}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-ink-900">
        {loading ? "..." : `${(salesKrw ?? 0).toLocaleString()}원`}
      </p>
      <p className="mt-0.5 text-xs tabular-nums text-ink-600">주문 {orders ?? 0}건</p>
    </Card>
  );
}
